import { USERS_INTELLIGENCE_ENDPOINTS } from "@/config/api";
import { httpClient } from "@/lib/http";
import type { ApiResponse } from "@/types/api";

import type {
    UsersIntelligenceDirectoryQueryParams,
    UsersIntelligenceDirectoryResponse,
    UsersIntelligenceMapQueryParams,
    UsersIntelligenceMapResponse,
    UsersIntelligenceSummary,
} from "../types";

const buildQueryString = (
    params: Record<string, string | number | boolean | undefined | null>
): string => {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
            return;
        }

        queryParams.append(key, String(value));
    });

    return queryParams.toString();
};

export const getUsersIntelligenceSummary = async (recentMinutes = 15) => {
    const queryString = buildQueryString({ recentMinutes });
    const url = queryString
        ? `${USERS_INTELLIGENCE_ENDPOINTS.SUMMARY}?${queryString}`
        : USERS_INTELLIGENCE_ENDPOINTS.SUMMARY;

    const { data } = await httpClient.get<ApiResponse<UsersIntelligenceSummary>>(url);
    return data;
};

export const getUsersIntelligenceDirectory = async (
    params: UsersIntelligenceDirectoryQueryParams = {}
) => {
    const queryString = buildQueryString({
        page: params.page ?? 0,
        size: params.size ?? 10,
        role: params.role,
        status: params.status,
        search: params.search,
        emailVerified:
            params.emailVerified === "" ? undefined : params.emailVerified,
        phoneVerified:
            params.phoneVerified === "" ? undefined : params.phoneVerified,
        ninVerified: params.ninVerified === "" ? undefined : params.ninVerified,
        onlineStatus: params.onlineStatus,
        appState: params.appState,
        hasRelatedAccounts:
            params.hasRelatedAccounts === ""
                ? undefined
                : params.hasRelatedAccounts,
        createdFrom: params.createdFrom,
        createdTo: params.createdTo,
    });
    const url = queryString
        ? `${USERS_INTELLIGENCE_ENDPOINTS.DIRECTORY}?${queryString}`
        : USERS_INTELLIGENCE_ENDPOINTS.DIRECTORY;

    const { data } = await httpClient.get<
        ApiResponse<UsersIntelligenceDirectoryResponse>
    >(url);
    return data;
};

export const getUsersIntelligenceMap = async (
    params: UsersIntelligenceMapQueryParams = {}
) => {
    const queryString = buildQueryString({
        role: params.role,
        status: params.status,
        onlineStatus: params.onlineStatus ?? "ONLINE",
        appState: params.appState,
        minLat: params.minLat,
        minLng: params.minLng,
        maxLat: params.maxLat,
        maxLng: params.maxLng,
        mode: params.mode ?? "CLUSTERS",
        gridSize: params.gridSize ?? 0.02,
        limit: params.limit ?? 250,
        lastSeenWithinMinutes: params.lastSeenWithinMinutes ?? 15,
        includeAdmins: params.includeAdmins ?? false,
    });
    const url = queryString
        ? `${USERS_INTELLIGENCE_ENDPOINTS.MAP}?${queryString}`
        : USERS_INTELLIGENCE_ENDPOINTS.MAP;

    const { data } = await httpClient.get<ApiResponse<UsersIntelligenceMapResponse>>(url);
    return data;
};
